import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import {
  Warning,
  Bug,
  ArrowLeft,
  CheckCircle,
  Trash,
  SpinnerGap,
  Clock,
  ArrowsClockwise,
  Browser,
  User as UserIcon,
} from '@phosphor-icons/react';

const typeTone = {
  error: 'bg-red-100 border-red-400 text-red-800',
  unhandledrejection: 'bg-amber-100 border-amber-400 text-amber-900',
  react_render: 'bg-purple-100 border-purple-400 text-purple-900',
};

export default function AdminClientErrors() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState({ total: 0, unresolved: 0, total_events: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open'); // open | resolved | all
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('userType') !== 'admin') navigate('/login');
  }, [navigate]);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'open') params.resolved = false;
      else if (filter === 'resolved') params.resolved = true;
      const r = await adminAPI.getClientErrors(params);
      setGroups(r.data?.groups || []);
      setStats({
        total: r.data?.total || 0,
        unresolved: r.data?.unresolved_groups || 0,
        total_events: r.data?.total_events || 0,
      });
    } catch (e) {
      toast({ title: 'Failed to load errors', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const openGroup = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const r = await adminAPI.getClientErrorGroup(id);
      setDetail(r.data);
    } catch (e) {
      toast({ title: 'Failed to load group', variant: 'destructive' });
    }
  };

  const toggleResolved = async (group) => {
    setActionBusy(true);
    try {
      await adminAPI.resolveClientError(group.id, !group.resolved);
      await fetchGroups();
      if (selectedId === group.id) await openGroup(group.id);
      toast({ title: group.resolved ? 'Marked as open' : 'Marked as resolved' });
    } catch (e) {
      toast({ title: 'Action failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setActionBusy(false);
    }
  };

  const deleteGroup = async (group) => {
    if (!window.confirm(`Delete this error group and all ${group.count} events?`)) return;
    setActionBusy(true);
    try {
      await adminAPI.deleteClientErrorGroup(group.id);
      if (selectedId === group.id) { setSelectedId(null); setDetail(null); }
      await fetchGroups();
      toast({ title: 'Group deleted' });
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="admin-client-errors-page">
      {/* Header */}
      <div className="bg-neutral-900 text-white py-6 border-b-4 border-black">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate('/admin')} className="text-white/80 hover:text-white" data-testid="back-to-admin-btn">
              <ArrowLeft size={26} weight="bold" />
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
                <Bug size={28} weight="fill" className="text-red-400" />
                Client Errors
              </h1>
              <p className="text-neutral-300 text-sm mt-1">
                Frontend exceptions captured from real users — no third-party SDKs.
              </p>
            </div>
          </div>
          <button
            onClick={fetchGroups}
            className="h-10 px-3 bg-neutral-800 hover:bg-neutral-700 text-white text-sm font-bold rounded-lg border-2 border-neutral-700 flex items-center gap-2"
            data-testid="refresh-errors-btn"
          >
            <ArrowsClockwise size={16} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="max-w-6xl mx-auto px-4 -mt-3 mb-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border-2 border-black rounded-xl p-3 shadow-[3px_3px_0px_#000]">
            <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">Open groups</p>
            <p className="text-2xl font-black text-red-600" data-testid="stat-open-groups">{stats.unresolved}</p>
          </div>
          <div className="bg-white border-2 border-black rounded-xl p-3 shadow-[3px_3px_0px_#000]">
            <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">All groups</p>
            <p className="text-2xl font-black text-neutral-900">{stats.total}</p>
          </div>
          <div className="bg-white border-2 border-black rounded-xl p-3 shadow-[3px_3px_0px_#000]">
            <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500">Total events</p>
            <p className="text-2xl font-black text-neutral-900">{stats.total_events}</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-5 space-y-3">
          <div className="flex gap-2 flex-wrap">
            {[
              { v: 'open', label: 'Open' },
              { v: 'resolved', label: 'Resolved' },
              { v: 'all', label: 'All' },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`px-3 h-9 text-xs font-bold rounded-lg border-2 ${filter === f.v ? 'bg-black text-white border-black' : 'bg-white text-neutral-700 border-neutral-300 hover:border-black'}`}
                data-testid={`errors-filter-${f.v}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-6 bg-white border-2 border-black rounded-xl flex items-center gap-3 text-neutral-600">
              <SpinnerGap size={22} className="animate-spin" /> Loading…
            </div>
          ) : groups.length === 0 ? (
            <div className="p-8 bg-white border-2 border-dashed border-neutral-300 rounded-xl text-center text-neutral-500" data-testid="errors-empty">
              <CheckCircle size={36} weight="light" className="mx-auto mb-2 text-emerald-500" />
              <p className="font-bold">No errors in this view.</p>
              <p className="text-xs mt-1">A quiet inbox is a happy inbox.</p>
            </div>
          ) : (
            groups.map((g) => (
              <button
                key={g.id}
                onClick={() => openGroup(g.id)}
                className={`w-full text-left bg-white border-2 rounded-xl p-4 shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] transition-all ${selectedId === g.id ? 'border-[#2B4C6F]' : 'border-black'} ${g.resolved ? 'opacity-60' : ''}`}
                data-testid={`error-row-${g.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-black text-sm text-neutral-900 truncate flex-1">{g.first_message}</p>
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded border-2 flex-shrink-0 ${typeTone[g.last_type] || 'bg-neutral-100 border-neutral-300 text-neutral-700'}`}>
                    {g.last_type?.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span className="flex items-center gap-1">
                    <Warning size={12} weight="fill" /> <span className="font-bold text-neutral-700">{g.count}</span> events
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} weight="bold" /> {g.last_seen ? new Date(g.last_seen).toLocaleString() : ''}
                  </span>
                </div>
                {g.last_url && (
                  <p className="text-xs text-neutral-400 mt-1 truncate font-mono">{g.last_url.replace(/^https?:\/\//, '')}</p>
                )}
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {!selectedId ? (
            <div className="bg-white border-2 border-dashed border-neutral-300 rounded-xl p-8 text-center text-neutral-500" data-testid="no-error-selected">
              <Bug size={48} weight="light" className="mx-auto mb-3 text-neutral-300" />
              <p className="font-bold">Select an error group</p>
              <p className="text-xs mt-1">Pick a row to see stack traces, browsers, and recent occurrences.</p>
            </div>
          ) : !detail ? (
            <div className="bg-white border-2 border-black rounded-xl p-6 flex items-center gap-3 text-neutral-600">
              <SpinnerGap size={22} className="animate-spin" /> Loading…
            </div>
          ) : (
            <div className="bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_#000] overflow-hidden" data-testid="error-detail">
              <div className="px-5 py-4 border-b-2 border-black flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-black text-base text-neutral-900 break-words">{detail.group.first_message}</p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {detail.group.count} events · first {new Date(detail.group.first_seen).toLocaleString()} · last {new Date(detail.group.last_seen).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleResolved(detail.group)}
                    disabled={actionBusy}
                    className={`h-9 px-3 text-xs font-bold rounded-lg border-2 disabled:opacity-50 ${detail.group.resolved ? 'bg-white text-neutral-700 border-neutral-300 hover:border-black' : 'bg-emerald-500 text-black border-black'}`}
                    data-testid="toggle-resolved-btn"
                  >
                    {detail.group.resolved ? 'Reopen' : 'Resolve'}
                  </button>
                  <button
                    onClick={() => deleteGroup(detail.group)}
                    disabled={actionBusy}
                    className="h-9 px-3 text-xs font-bold rounded-lg border-2 bg-red-500 text-white border-black disabled:opacity-50"
                    data-testid="delete-group-btn"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-5">
                {/* Most recent stack */}
                {detail.samples[0]?.stack && (
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500 mb-1">Most recent stack</p>
                    <pre className="text-xs font-mono bg-neutral-900 text-neutral-100 p-3 rounded-lg overflow-x-auto border-2 border-black max-h-72 overflow-y-auto" data-testid="error-stack">
{detail.samples[0].stack}
                    </pre>
                  </div>
                )}

                {detail.samples[0]?.component_stack && (
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500 mb-1">React component stack</p>
                    <pre className="text-xs font-mono bg-neutral-100 text-neutral-800 p-3 rounded-lg overflow-x-auto border-2 border-neutral-300 max-h-48 overflow-y-auto">
{detail.samples[0].component_stack}
                    </pre>
                  </div>
                )}

                {/* Sample table */}
                <div>
                  <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500 mb-2">
                    Recent occurrences ({detail.samples.length})
                  </p>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {detail.samples.map((s) => (
                      <div key={s.id} className="border-2 border-neutral-200 rounded-lg p-3 text-xs">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-neutral-500">
                          <span className="flex items-center gap-1">
                            <Clock size={12} weight="bold" /> {new Date(s.created_at).toLocaleString()}
                          </span>
                          {s.user_email && (
                            <span className="flex items-center gap-1 text-neutral-700">
                              <UserIcon size={12} weight="bold" /> {s.user_email} ({s.user_type})
                            </span>
                          )}
                          {s.user_agent && (
                            <span className="flex items-center gap-1 truncate max-w-xs text-neutral-500">
                              <Browser size={12} weight="bold" /> {parseUA(s.user_agent)}
                            </span>
                          )}
                        </div>
                        {s.url && (
                          <p className="text-neutral-600 mt-1 font-mono truncate">{s.url}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function parseUA(ua) {
  if (!ua) return 'Unknown';
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  let os = '';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  return `${browser}${os ? ' · ' + os : ''}`;
}
